import { inject, observer } from "mobx-react";
import * as React from "react";
import { Alert, AlertDescription, AlertTitle, Card, Progress, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "randmarcomps";
import { useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery } from "randmar-api-client";
import { FormattedNumber } from "react-intl";
import { Link, useParams } from "react-router-dom";
import VendorValues from "../components/categories/VendorValues";
import InvoicesGrid from "../components/grids/InvoicesGrid";
import PurchaseOrdersGrid from "../components/grids/PurchaseOrdersGrid";
import ReceiptsGrid from "../components/grids/ReceiptsGrid";
import CurrencyUtils from "../components/util/CurrencyUtils";
import FormatDateFromInt, { getCurrentDate } from "../components/util/DateTimeUtil";
import { DocumentHeader } from "../components/util/StyledComponents";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

interface IProps {
  store?: RootStore;
}

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

const Operations: React.FC<IProps> = (props) => {
  const { store } = props;
  const headingId = React.useId();
  const { applicationId } = useManufacturer();
  const params = useParams();
  const { data: inventoryValue, isLoading: isInventoryLoading } = useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery({ routeManufacturerId: applicationId });
  const [otherLoading, setOtherLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      if (!store) return;
      const { documentsStore } = store;

      await documentsStore.getInvoices(applicationId);
      await documentsStore.getPurchasesOrders(applicationId);
      await documentsStore.getReceipts(applicationId);

      setOtherLoading(false);
    };

    fetchData();
  }, [applicationId, store]);

  const loading = isInventoryLoading || otherLoading;
  const statusMessage = loading ? "Loading operations data" : "Operations data refreshed.";
  const todayKey = React.useMemo(() => FormatDateFromInt(getCurrentDate()) || "", []);
  const todayLabel = React.useMemo(() => {
    if (!todayKey) return "today";
    const parsed = new Date(todayKey);
    if (Number.isNaN(parsed.getTime())) return todayKey;
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(parsed);
  }, [todayKey]);

  if (!store) {
    return <Progress />;
  }

  const { subSection } = params;
  const { documentsStore: { requisitions, invoices, purchaseOrders, receipts } } = store;

  const totalInventoryValue = React.useMemo(() => (inventoryValue || []).reduce((sum, value) => sum + (value.InventoryValue || 0), 0), [inventoryValue]);
  const warehouseCount = React.useMemo(() => {
    const entries = (inventoryValue || [])
      .map(entry => entry.Location)
      .filter((location): location is string => Boolean(location));
    return new Set(entries).size;
  }, [inventoryValue]);

  const purchaseOrderSummary = React.useMemo(() => {
    const list = purchaseOrders || [];
    return {
      count: list.length,
      value: list.reduce((sum, value) => sum + (value.subTotal || 0), 0)
    };
  }, [purchaseOrders]);

  const todaysReceiptsSummary = React.useMemo(() => {
    const list = receipts || [];
    return list.reduce(
      (acc, receipt) => {
        const normalizedDate = typeof receipt.receiptDate === "number" ? FormatDateFromInt(receipt.receiptDate) : receipt.receiptDate || "";
        if (!todayKey || normalizedDate === todayKey) {
          acc.count += 1;
          acc.value += receipt.subTotal || 0;
        }
        return acc;
      },
      { count: 0, value: 0 }
    );
  }, [receipts, todayKey]);

  const invoiceBreakdown = React.useMemo(() => {
    const list = invoices || [];
    const outstandingInvoices = list.filter(i => i.payed === false && i.documentType === "Invoice");
    const outstandingCredits = list.filter(i => i.payed === false && i.documentType === "Credit Note");
    const sumTotals = (items: typeof list) => items.reduce((sum, value) => sum + (value.subTotal || 0), 0);

    return {
      invoices: {
        count: outstandingInvoices.length,
        value: sumTotals(outstandingInvoices)
      },
      credits: {
        count: outstandingCredits.length,
        value: sumTotals(outstandingCredits)
      }
    };
  }, [invoices]);

  const currentTab = subSection || "Overview";
  const disableSummaryLinks = loading;

  return (
    <main className="operations-layout pb-10" aria-labelledby={headingId} aria-busy={loading}>
      <div className="sr-only" role="status" aria-live="polite">{statusMessage}</div>
      {loading ? <Progress aria-label="Loading operations data" /> : <Progress value={100} aria-label="Operations data loaded" />}

      <header>
        <DocumentHeader justifyContent="space-between" alignItems="center" direction="row">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Randmar</p>
            <h1 id={headingId} className="text-3xl font-semibold tracking-tight text-slate-900">Operations</h1>
            <p className="text-sm text-slate-500">Inventory, purchase orders, receipts, and invoices in one responsive workspace.</p>
            {loading && <Progress className="mt-3 h-1" aria-label="Refreshing operations data" />}
          </div>
        </DocumentHeader>
      </header>

      <Alert variant="info" className="ops-panel" aria-live="polite">
        <AlertTitle>What are Operations?</AlertTitle>
        <AlertDescription>
          Track Randmar inventory, inbound purchase orders, receipts, and invoice balances. Use the tabs to dive deeper without leaving your keyboard.
        </AlertDescription>
      </Alert>

      <section aria-label="Operations sections">
        <Tabs value={currentTab} className="flex flex-col gap-6" aria-label="Operations subsections">
          <div className="ops-panel">
            <TabsList className="ops-tablist w-full" aria-label="Operations navigation">
              <TabsTrigger value="Overview" className="ops-tab-trigger" asChild>
                <Link to={Path.operationsSubSection(applicationId, "Overview")} title="Overview of Randmar warehouses">Overview</Link>
              </TabsTrigger>
              <TabsTrigger value="PurchaseOrders" className="ops-tab-trigger" asChild>
                <Link to={Path.operationsSubSection(applicationId, "PurchaseOrders")} title="All purchase orders">Purchase Orders</Link>
              </TabsTrigger>
              <TabsTrigger value="Receipts" className="ops-tab-trigger" asChild>
                <Link to={Path.operationsSubSection(applicationId, "Receipts")} title="Inventory received at Randmar">Receipts</Link>
              </TabsTrigger>
              <TabsTrigger value="Invoices" className="ops-tab-trigger" asChild>
                <Link to={Path.operationsSubSection(applicationId, "Invoices")} title="Invoices for Randmar POs">Invoices</Link>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="Overview" forceMount className="flex flex-col gap-6" aria-label="Overview tab content">
            <Card className="ops-panel" data-busy={loading} aria-live="polite">
              <div className="ops-metric-grid">
                <article className="ops-metric-card" title="Value of inventory across warehouses">
                  <p className="ops-metric-label">Total Inventory Value</p>
                  <div className="ops-metric-value">
                    {loading ? <Skeleton aria-hidden className="h-10 w-40 rounded-lg" /> : <FormattedNumber value={totalInventoryValue} {...CurrencyUtils.format} />}
                  </div>
                  <p className="ops-metric-footnote">Across {warehouseCount || 0} warehouses</p>
                </article>

                <Link
                  to={Path.purchaseOrders(applicationId)}
                  title="Open purchase orders"
                  className={cx("ops-metric-card ops-metric-card--link", disableSummaryLinks && "ops-metric-card--disabled")}
                  aria-label="Review open purchase orders"
                  aria-disabled={disableSummaryLinks}
                  onClick={disableSummaryLinks ? (event) => event.preventDefault() : undefined}
                  tabIndex={disableSummaryLinks ? -1 : undefined}
                >
                  <p className="ops-metric-label">On Purchase Orders</p>
                  <div className="ops-metric-value">
                    {loading ? <Skeleton aria-hidden className="h-10 w-32 rounded-lg" /> : <FormattedNumber value={purchaseOrderSummary.value} {...CurrencyUtils.format} />}
                  </div>
                  <div className="ops-metric-stack">
                    <span>Open POs</span>
                    <strong>{purchaseOrderSummary.count}</strong>
                  </div>
                  <p className="ops-metric-footnote">Tap to review approval-ready documents</p>
                </Link>

                <Link
                  to={Path.receipts(applicationId)}
                  title="Inventory received at Randmar today"
                  className={cx("ops-metric-card ops-metric-card--link", disableSummaryLinks && "ops-metric-card--disabled")}
                  aria-label={`View receipts posted ${todayLabel}`}
                  aria-disabled={disableSummaryLinks}
                  onClick={disableSummaryLinks ? (event) => event.preventDefault() : undefined}
                  tabIndex={disableSummaryLinks ? -1 : undefined}
                >
                  <p className="ops-metric-label">Received {todayLabel}</p>
                  <div className="ops-metric-value">
                    {loading ? <Skeleton aria-hidden className="h-10 w-28 rounded-lg" /> : <FormattedNumber value={todaysReceiptsSummary.value} {...CurrencyUtils.format} />}
                  </div>
                  <p className="ops-metric-footnote">{todaysReceiptsSummary.count} receipts logged</p>
                </Link>

                <Link
                  to={Path.invoices(applicationId)}
                  title="Outstanding invoices and credits"
                  className={cx("ops-metric-card ops-metric-card--link", disableSummaryLinks && "ops-metric-card--disabled")}
                  aria-label="Review outstanding invoices and credits"
                  aria-disabled={disableSummaryLinks}
                  onClick={disableSummaryLinks ? (event) => event.preventDefault() : undefined}
                  tabIndex={disableSummaryLinks ? -1 : undefined}
                >
                  <p className="ops-metric-label">Invoices & Credits</p>
                  <div className="ops-dual-stack">
                    <div>
                      <div className="ops-metric-value ops-metric-value--compact">
                        {loading ? <Skeleton aria-hidden className="h-8 w-24 rounded-lg" /> : <FormattedNumber value={invoiceBreakdown.invoices.value} {...CurrencyUtils.format} />}
                      </div>
                      <p className="ops-metric-footnote">{invoiceBreakdown.invoices.count} invoices pending</p>
                    </div>
                    <div>
                      <div className="ops-metric-value ops-metric-value--compact">
                        {loading ? <Skeleton aria-hidden className="h-8 w-24 rounded-lg" /> : <FormattedNumber value={invoiceBreakdown.credits.value} {...CurrencyUtils.format} />}
                      </div>
                      <p className="ops-metric-footnote">{invoiceBreakdown.credits.count} credits open</p>
                    </div>
                  </div>
                </Link>
              </div>
            </Card>

            <section className="ops-panel ops-panel--flush" aria-label="Warehouse inventory values">
              <VendorValues inventoryValues={inventoryValue || []} requisitions={requisitions || []} />
            </section>
          </TabsContent>

          <TabsContent value="PurchaseOrders" forceMount className="space-y-4" aria-label="Purchase orders tab content">
            <PurchaseOrdersGrid purchaseOrders={purchaseOrders} />
          </TabsContent>

          <TabsContent value="Receipts" forceMount className="space-y-4" aria-label="Receipts tab content">
            <ReceiptsGrid receipts={receipts} />
          </TabsContent>

          <TabsContent value="Invoices" forceMount className="space-y-4" aria-label="Invoices tab content">
            <InvoicesGrid invoices={invoices} />
          </TabsContent>
        </Tabs>
      </section>
    </main>
  );
};

export default withRoot(inject("store")(observer(Operations)));
